import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, HardDrive } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/contexts/I18nContext';
import { createNVR, type NVRUpsert } from '@/api/nvrs';
import { extractApiError } from '@/utils/apiError';

interface Props {
  zoneId: number | string;
  onCreated: () => void;
  onCancel: () => void;
  /** Pre-fill the IP input on create. Used by the LC peer-discovery
   *  flow to seed the form from a discovered host's IP. */
  initialIp?: string;
}

/**
 * Inline new-NVR form used by `AddDeviceDropdown → NVR`.
 *
 * Field set mirrors the writable subset of
 * `services/plugins/equipment_discovery/api/nvrs.py:WRITABLE_FIELDS`
 * minus the identity columns (`model`, `serial_number`,
 * `firmware_version`, `device_id`, `mac_address`) — those are filled
 * by the discovery primitive autofill, not by hand. The vendor is
 * a read-only "hikvision" pill today; when a second vendor lands we
 * swap it for a `<Select>`.
 *
 * The "Discover from jetson" affordance is intentionally NOT wired
 * here — task #8 (`<DiscoverButton>` + `useDiscover()`) lands first,
 * then we add the call site in a follow-up.
 */
export default function NewNVRCard({ zoneId, onCreated, onCancel, initialIp }: Props) {
  const { t } = useT();
  const [ipAddress, setIpAddress] = useState(initialIp ?? '');
  const [port, setPort] = useState('80');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!ipAddress.trim()) {
      setError(t('equipment.newNVR.ipAddressRequired'));
      return;
    }
    setSubmitting(true);
    const portNumber = Number(port);
    const payload: NVRUpsert = {
      ip_address: ipAddress.trim(),
      port: Number.isFinite(portNumber) && portNumber > 0 ? portNumber : 80,
      username: username.trim(),
      password,
      vendor: 'hikvision',
      notes,
    };
    try {
      await createNVR(zoneId, payload);
      onCreated();
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('error.general')));
      } else {
        setError(t('error.general'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <HardDrive className="size-4" /> {t('equipment.newNVR.title')}
        </h2>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="new-nvr-ip">{t('equipment.newNVR.ipAddress')}</Label>
              <Input
                id="new-nvr-ip"
                value={ipAddress}
                onChange={e => setIpAddress(e.target.value)}
                placeholder="192.168.1.65"
                className="font-mono"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-nvr-port">{t('equipment.newNVR.port')}</Label>
              <Input
                id="new-nvr-port"
                type="number"
                value={port}
                onChange={e => setPort(e.target.value)}
                placeholder="80"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-nvr-vendor">{t('equipment.newNVR.vendor')}</Label>
              <Input
                id="new-nvr-vendor"
                value="hikvision"
                readOnly
                className="font-mono bg-muted"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-nvr-user">{t('equipment.newNVR.username')}</Label>
              <Input
                id="new-nvr-user"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="font-mono"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-nvr-pw">{t('equipment.newNVR.password')}</Label>
              <Input
                id="new-nvr-pw"
                name="nvr-new-secret"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="font-mono"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-nvr-notes">{t('equipment.newNVR.notes')}</Label>
            <textarea
              id="new-nvr-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </div>
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
